import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { leadsAPI, sellerSheetAPI } from "@/lib/api";
import SellerSheetForm, { calculateLeadScore } from "@/components/SellerSheetForm";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  Phone,
  User,
  Calendar,
  MapPin,
  Tag,
  Hash,
  Activity,
  Gauge,
  Info,
  Home,
  MapPinned,
  Clock,
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

const OBJEKTART_LABELS = {
  ETW: "Eigentumswohnung",
  MFH: "Mehrfamilienhaus",
  EFH: "Einfamilienhaus",
  DHH: "Doppelhaushälfte",
  RH: "Reihenhaus",
  RMH: "Reihenmittelhaus",
  REH: "Reihenendhaus",
  Grundstück: "Grundstück",
  Sonstiges: "Sonstiges",
};

const getDisplayValue = (value) => {
  if (value && typeof value === "object" && "value" in value) {
    return value.value;
  }
  return value ?? "";
};

// Display label formatters for user-friendly presentation
const formatStatusLabel = (status) => {
  const statusLabels = {
    "rueckruf offen": "Rückruf offen",
    "rückruf offen": "Rückruf offen",
    "pruefen": "Prüfen",
    "prüfen": "Prüfen",
    "qualifiziert": "Qualifiziert",
    "besichtigung": "Besichtigung",
    "besichtigung terminiert": "Besichtigung terminiert",
    "abgelehnt": "Abgelehnt",
  };
  const raw = getDisplayValue(status);
  return statusLabels[raw?.toLowerCase()] || raw || "-";
};

const formatSourceLabel = (source) => {
  const sourceLabels = {
    "online": "Online",
    "flyer": "Flyer",
    "schilder": "Schilder",
    "print": "Print",
    "fahrzeuge": "Fahrzeuge",
    "sonstiges": "Sonstiges",
  };
  const raw = getDisplayValue(source);
  return sourceLabels[raw?.toLowerCase()] || raw || "-";
};

const STATUS_COLORS = {
  "rückruf offen": "bg-orange-500 text-white",
  "rueckruf offen": "bg-orange-500 text-white",
  "pruefen": "bg-amber-500 text-white",
  "prüfen": "bg-amber-500 text-white",
  "qualifiziert": "bg-emerald-100 text-emerald-800",
  "besichtigung terminiert": "bg-[#0A4F53] text-white",
  "besichtigung": "bg-[#0A4F53] text-white",
  "abgelehnt": "bg-red-600 text-white",
};

const DECISION_TO_STATUS = {
  "Ablehnen": "abgelehnt",
  "Prüfen": "pruefen",
  "Besichtigung": "besichtigung",
  "ablehnen": "abgelehnt",
  "prüfen": "pruefen",
  "besichtigung": "besichtigung",
};

// Final outcomes that LOCK the workflow status (Priority 1 in 6-tier hierarchy)
const FINAL_OUTCOMES = new Set(["deal", "no_deal", "abgelehnt", "vermittelt"]);

/**
 * Detect a real visit appointment: date AND time both present and time != 00:00.
 * Mirrors the backend rule in _derive_priority_inputs_from_sheet.
 */
function hasVisitAppointment(sheet) {
  if (!sheet) return false;
  const dateRaw = sheet["9_besichtigungstermin"];
  if (!dateRaw) return false;
  const zeitRaw = sheet["9_besichtigungstermin_zeit"] || "";
  try {
    const d = new Date(dateRaw);
    if (Number.isNaN(d.getTime())) return false;
    // Backend stores merged UTC ISO; compute Berlin wall-clock hour/minute.
    const berlinParts = new Intl.DateTimeFormat("en-GB", {
      timeZone: "Europe/Berlin",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    })
      .formatToParts(d)
      .reduce((a, p) => ({ ...a, [p.type]: p.value }), {});
    const h = parseInt(berlinParts.hour || "0", 10);
    const m = parseInt(berlinParts.minute || "0", 10);
    if (h !== 0 || m !== 0) return true;
    // Legacy fallback: companion `_zeit` field still carries the time.
    if (typeof zeitRaw === "string" && zeitRaw.includes(":")) {
      const [hh, mm] = zeitRaw.split(":");
      return parseInt(hh || "0", 10) !== 0 || parseInt(mm || "0", 10) !== 0;
    }
  } catch {
    return false;
  }
  return false;
}

/**
 * Resolve effective workflow status using strict priority hierarchy.
 *  1. Final Outcome (lead_outcome ∈ FINAL_OUTCOMES) → LOCKED
 *  2. KO Criterion active → "abgelehnt"
 *  3. Manual Decision → mapped status
 *  4. Visit appointment set (date + time)  → "besichtigung terminiert"
 *  5. Nachkontakt completed → "qualifiziert" (server-controlled)
 *  6. Default → lead.status
 */
function resolveSidebarStatus({ leadStatus, sheet }) {
  const leadStatusRaw = getDisplayValue(leadStatus) || "";
  const lo = (getDisplayValue(sheet?.lead_outcome) || "").toString().toLowerCase();

  // Priority 1: final outcome locks
  if (FINAL_OUTCOMES.has(lo)) {
    return { status: leadStatusRaw, overrideReason: null };
  }

  // Priority 2: KO criterion
  const ko = getDisplayValue(sheet?.["8_KO_kriterium"]);
  if (ko) {
    return { status: "abgelehnt", overrideReason: "KO-Kriterium aktiv" };
  }

  // Priority 3: Manual decision
  const manual =
    getDisplayValue(sheet?.["9_status_ziel"]) ||
    getDisplayValue(sheet?.manuelle_entscheidung);
  if (manual && DECISION_TO_STATUS[manual]) {
    return {
      status: DECISION_TO_STATUS[manual],
      overrideReason: "Manuelle Ueberschreibung aktiv",
    };
  }

  // Priority 4: Visit appointment scheduled
  if (hasVisitAppointment(sheet)) {
    return {
      status: "besichtigung terminiert",
      overrideReason: "Besichtigung terminiert",
    };
  }

  // Priority 5 & 6: server-controlled
  return { status: leadStatusRaw, overrideReason: null };
}

function formatDateTime(dateStr) {
  if (!dateStr) return "-";
  try {
    const date = new Date(dateStr);
    return format(date, "dd.MM.yyyy, HH:mm");
  } catch {
    return dateStr || "-";
  }
}

function InfoRow({ icon: Icon, label, value, testId, highlight }) {
  return (
    <div>
      <span className="text-[10px] font-semibold text-[#8A9198] uppercase tracking-wider flex items-center gap-1.5 mb-0.5">
        <Icon className="h-3 w-3" />
        {label}
      </span>
      <p
        className={`text-sm font-medium ${highlight ? "text-[#0A4F53]" : "text-[#1C1F22]"}`}
        data-testid={testId}
      >
        {value || "-"}
      </p>
    </div>
  );
}

const SCORE_COLORS = {
  red: { bg: "bg-red-500", track: "bg-red-100", text: "text-red-700", ring: "ring-red-200" },
  yellow: { bg: "bg-amber-500", track: "bg-amber-100", text: "text-amber-700", ring: "ring-amber-200" },
  green: { bg: "bg-emerald-500", track: "bg-emerald-100", text: "text-emerald-700", ring: "ring-emerald-200" },
  none: { bg: "bg-gray-300", track: "bg-gray-100", text: "text-gray-500", ring: "ring-gray-200" },
};

function ScoreIndicator({ sheet }) {
  let pct, label, level;

  // Prefer Baserow authoritative score when available (real mode)
  if (sheet?.score_gesamt != null && sheet.score_gesamt !== "") {
    pct = Math.round(Number(sheet.score_gesamt));
    // Safely extract KO value from potential Baserow single-select object
    const koValue = getDisplayValue(sheet["8_KO_kriterium"]);
    const isKO = koValue && koValue !== "";
    if (isKO) {
      pct = 0;
      label = "Nicht weiterverfolgen";
      level = "red";
    } else if (getDisplayValue(sheet?.entscheidung)) {
      label = String(getDisplayValue(sheet.entscheidung));
      level = pct >= 60 ? "green" : pct >= 30 ? "yellow" : "red";
    } else {
      level = pct >= 60 ? "green" : pct >= 30 ? "yellow" : "red";
      label = pct >= 60 ? "Termin empfohlen" : pct >= 30 ? "Pruefen" : "Niedrige Prioritaet";
    }
  } else {
    // Mock mode fallback: use frontend-calculated score
    ({ pct, label, level } = calculateLeadScore(sheet));
  }

  const colors = SCORE_COLORS[level] || SCORE_COLORS.none;
  const isBaserow = sheet?.score_gesamt != null && sheet.score_gesamt !== "";

  return (
    <div className="px-6 pt-5 pb-5 mb-1" data-testid="score-indicator">
      <div className="flex items-center justify-between mb-2.5">
        <span className="text-[10px] font-bold text-[#8A9198] uppercase tracking-widest flex items-center gap-1.5">
          <Gauge className="h-3.5 w-3.5" />
          Bewertung
        </span>
        <span className={`text-xs font-semibold ${colors.text}`} data-testid="score-label">
          {label}
        </span>
      </div>
      <div className={`h-2.5 rounded-full ${colors.track} overflow-hidden`}>
        <div
          className={`h-full rounded-full ${colors.bg} transition-all duration-500 ease-out`}
          style={{ width: `${Math.max(pct, 3)}%` }}
          data-testid="score-bar"
        />
      </div>
      <div className="flex items-center justify-between mt-2">
        <span className={`text-lg font-bold ${colors.text} tabular-nums`} data-testid="score-value">
          {pct}%
        </span>
        <span className="text-[9px] text-[#8A9198] flex items-center gap-1">
          <Info className="h-2.5 w-2.5" />
          {isBaserow ? "Baserow Score" : "Provisorische Vorschau"}
        </span>
      </div>
    </div>
  );
}

export default function LeadDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [lead, setLead] = useState(null);
  const [sheet, setSheet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [liveForm, setLiveForm] = useState(null);

  useEffect(() => {
    setLoading(true);
    leadsAPI
      .detail(id)
      .then((res) => {
        setLead(res.data.lead);
        setSheet(res.data.sellerSheet);
      })
      .catch(() => {
        toast.error("Lead konnte nicht geladen werden");
        navigate("/leads");
      })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const refetchDetail = useCallback(async () => {
    try {
      const res = await leadsAPI.detail(id);
      setLead(res.data.lead);
      setSheet(res.data.sellerSheet);
    } catch (e) {
      console.warn("Detail refetch failed", e);
    }
  }, [id]);

  const handleSave = useCallback(
    async (formData) => {
      if (!sheet || !lead) return;
      setSaving(true);
      try {
        const { data } = await sellerSheetAPI.update(sheet.id, {
          ...formData,
          _lead_row_id: lead.id,
          // Ordinary save - no _is_complete flag
        });

if (data?._manual_decision_cleared) {
  data.manuelle_entscheidung = "";
  data["9_status_ziel"] = "";
}

        // Update sheet and lead state immediately after successful save
        const savedAt = new Date().toISOString();

        setSheet({
          ...data,
          last_updated_at: savedAt,
        });

        setLead((prev) =>
          prev
            ? {
                ...prev,
                last_updated_at: savedAt,
                ...(data._updated_lead_status ? { status: data._updated_lead_status } : {}),
                ...(data._updated_lead_name ? { name: data._updated_lead_name } : {}),
                ...(data._updated_lead_phone ? { phone: data._updated_lead_phone } : {}),
              }
            : prev
        );

        // Show appropriate toast based on _save_status
        const saveStatus = data._save_status || "success";
        if (saveStatus === "success") {
          toast.success("Zwischengespeichert");
        } else if (saveStatus === "partial_success") {
          // Partial success - seller sheet saved but some fields may have been omitted
          toast.success("Zwischengespeichert");
          if (data._warnings && data._warnings.length > 0) {
            console.warn("Save warnings:", data._warnings);
          }
        }

        // Refetch to pull the fresh Baserow-computed score (formula fields) so
        // UI score == Baserow score == Telegram/n8n score.
        await refetchDetail();
      } catch (err) {
        // Only show error for actual seller sheet save failure
        toast.error("Speichern fehlgeschlagen");
        console.error("Save error:", err);
      } finally {
        setSaving(false);
      }
    },
    [sheet, lead, refetchDetail]
  );

  const handleComplete = useCallback(
    async (formData) => {
      if (!sheet || !lead) return;
      setSaving(true);
      try {
        const updated = {
          ...formData,
          "9_nachkontakt_abgeschlossen": true,
          _lead_row_id: lead.id,
          _is_complete: true,  // Signal this is an explicit Nachkontakt completion
        };
        const { data } = await sellerSheetAPI.update(sheet.id, updated);
        
        // Update lead with all changed fields from response
        setLead((prev) => {
          if (!prev) return prev;
          const updates = {};
          if (data._updated_lead_status) {
            updates.status = data._updated_lead_status;
          }
          if (data._updated_lead_name) {
            updates.name = data._updated_lead_name;
          }
          if (data._updated_lead_phone) {
            updates.phone = data._updated_lead_phone;
          }
          return Object.keys(updates).length > 0 ? { ...prev, ...updates } : prev;
        });
        
        // Update sheet data
if (
  data?.["9_status_ziel"] === null ||
  data?.["9_status_ziel"] === ""
) {
  data["9_status_ziel"] = "";
  data["manuelle_entscheidung"] = "";
}

setSheet(data);
        
        // Show appropriate toast based on _save_status
        const saveStatus = data._save_status || "success";
        if (saveStatus === "success" || saveStatus === "partial_success") {
          toast.success("Nachkontakt abgeschlossen");
        }

        // Refetch to pull fresh Baserow-computed score (formula fields).
        await refetchDetail();
      } catch (err) {
        toast.error("Abschluss fehlgeschlagen");
        console.error("Complete error:", err);
      } finally {
        setSaving(false);
      }
    },
    [sheet, lead, refetchDetail]
  );

  if (loading) {
    return (
      <div
        className="h-screen flex items-center justify-center text-[#5E646A] text-sm"
        data-testid="detail-loading"
      >
        Lead wird geladen...
      </div>
    );
  }

  if (!lead) return null;

  return (
    <div className="h-screen flex flex-col" data-testid="lead-detail-page">
      {/* Header */}
      <header className="bg-white border-b border-[#E2E4E9] px-6 py-3 flex items-center gap-4 shrink-0">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/leads")}
          className="text-[#5E646A] hover:text-[#1C1F22] -ml-2"
          data-testid="back-button"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Zurueck
        </Button>
        <div className="h-5 w-px bg-[#E2E4E9]" />
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-[#1C1F22]">
            {lead.name}
          </span>
          <span className="text-xs text-[#5E646A] font-mono">
            {lead.lead_id}
          </span>
        </div>
      </header>

      {/* Two-Column Layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* LEFT: Read-Only Lead Data */}
        <aside
          className="w-[340px] shrink-0 bg-[#F0F2F5] border-r border-[#E2E4E9] overflow-y-auto"
          data-testid="lead-sidebar"
        >
          {/* Score Indicator */}
          <ScoreIndicator sheet={liveForm || sheet} />

          <div className="px-6 pb-6">
            <h2 className="text-xs font-bold text-[#5E646A] uppercase tracking-widest mb-5">
              Lead-Daten
            </h2>

            <div className="space-y-4">
              <InfoRow
                icon={Hash}
                label="Lead-ID"
                value={lead.lead_id}
                testId="lead-field-id"
              />
              <InfoRow
                icon={Calendar}
                label="Erstellt"
                value={formatDateTime(lead.created_at)}
                testId="lead-field-created"
              />
              <InfoRow
                icon={Clock}
                label="Zuletzt gespeichert"
		value={
  		sheet?.last_updated_at
    		? formatDateTime(sheet.last_updated_at)
    		: liveForm?.last_updated_at
      		? formatDateTime(liveForm.last_updated_at)
      		: "Noch nicht gespeichert"
		}
                testId="lead-field-last-updated"
              />
              <InfoRow
                icon={MapPin}
                label="Quelle"
                value={formatSourceLabel(lead.source)}
                testId="lead-field-source"
              />
              <InfoRow
                icon={Tag}
                label="Kampagne"
                value={lead.campaign}
                testId="lead-field-campaign"
              />

              <div className="border-t border-[#E2E4E9] pt-4">
                <InfoRow
                  icon={User}
                  label="Name"
                  value={lead.name}
                  testId="lead-field-name"
                />
              </div>
              <InfoRow
                icon={Phone}
                label="Telefon"
                value={lead.phone}
                testId="lead-field-phone"
                highlight
              />

            {/* Object Info from Seller Sheet */}
            <ObjectInfoBlock sheet={liveForm || sheet} />

              <div className="border-t border-[#E2E4E9] pt-4">
                <span className="text-xs font-semibold text-[#5E646A] uppercase tracking-wider flex items-center gap-1.5 mb-1.5">
                  <Activity className="h-3 w-3" />
                  Workflow-Status
                </span>
{(() => {
  const effective = resolveSidebarStatus({
    leadStatus: lead.status,
    sheet: liveForm || sheet,
  });
  const displayStatus = effective.status || "-";
  const statusKey = (displayStatus || "").toString().toLowerCase();
  const isOverride = !!effective.overrideReason;

  return (
    <>
      <span
        className={`inline-flex items-center px-2.5 py-1 rounded text-xs font-medium ${
          STATUS_COLORS[statusKey] || "bg-gray-100 text-gray-800"
        }`}
        data-testid="lead-field-status"
      >
        {formatStatusLabel(displayStatus)}
      </span>

      {isOverride && (
        <div
          className="mt-2 px-2.5 py-1.5 bg-[#F0FAF9] border border-[#0A4F53]/20 rounded-md"
          data-testid="manual-override-badge"
        >
          <p className="text-[10px] font-bold text-[#0A4F53] uppercase tracking-wider">
            {effective.overrideReason}
          </p>
        </div>
      )}

      {!isOverride && (
        <p className="text-[10px] text-[#8A9198] mt-1.5">
          Automatisch gesteuert (n8n)
        </p>
      )}
    </>
  );
})()}
              </div>
            </div>

            {/* Call Button */}
            <div className="mt-6">
              <a href={`tel:${lead.phone}`} data-testid="call-link">
                <Button
                  className="w-full bg-[#0A4F53] hover:bg-[#073B3E] text-white font-medium h-11"
                  data-testid="call-button"
                >
                  <Phone className="h-4 w-4 mr-2" />
                  Anrufen
                </Button>
              </a>
            </div>
          </div>
        </aside>

        {/* RIGHT: Seller Sheet Form */}
        <main
          className="flex-1 flex flex-col overflow-hidden bg-white"
          data-testid="seller-sheet-area"
        >
          {sheet ? (
            <SellerSheetForm
              data={sheet}
              onSave={handleSave}
              onComplete={handleComplete}
              saving={saving}
              isAdmin={user?.role === "admin"}
              onFormChange={setLiveForm}
            />
          ) : (
            <div className="flex items-center justify-center h-full text-[#5E646A] text-sm">
              Verkaeuferdatenblatt wird erstellt...
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

function ObjectInfoBlock({ sheet }) {
  const objektart = getDisplayValue(sheet?.["2_objektart"]);
const OBJEKTART_LABELS = {
  ETW: "Eigentumswohnung",
  MFH: "Mehrfamilienhaus",
  EFH: "Einfamilienhaus",
  DHH: "Doppelhaushälfte",
  RH: "Reihenhaus",
  RMH: "Reihenmittelhaus",
  REH: "Reihenendhaus",
  Grundstück: "Grundstück",
  Sonstiges: "Sonstiges",
};
const objektartLabel = objektart
  ? OBJEKTART_LABELS[objektart] || objektart
  : null;
  const adresse = getDisplayValue(sheet?.["2_adresse"]);
  const plz = getDisplayValue(sheet?.["2_plz"]);
  const stadt = getDisplayValue(sheet?.["2_stadt"]);

  const hasAddress = adresse || plz || stadt;
  const addressParts = [adresse, [plz, stadt].filter(Boolean).join(" ")].filter(Boolean).join(", ");

  if (!objektart && !hasAddress) {
    return (
      <div className="border-t border-[#E2E4E9] mt-4 pt-4" data-testid="object-info-block">
        <span className="text-[10px] font-bold text-[#8A9198] uppercase tracking-widest flex items-center gap-1.5 mb-2">
          <Home className="h-3 w-3" />
          Objekt
        </span>
        <p className="text-xs text-[#8A9198] italic">Noch keine Objektdaten erfasst</p>
      </div>
    );
  }

  return (
    <div className="border-t border-[#E2E4E9] mt-4 pt-4" data-testid="object-info-block">
      <span className="text-[10px] font-bold text-[#8A9198] uppercase tracking-widest flex items-center gap-1.5 mb-2">
        <Home className="h-3 w-3" />
        Objekt
      </span>
      {objektartLabel && (
        <p className="text-sm font-medium text-[#1C1F22] mb-1" data-testid="sidebar-objektart">
          {objektartLabel}
        </p>
      )}
      {hasAddress && (
        <p className="text-xs text-[#5E646A] flex items-start gap-1" data-testid="sidebar-adresse">
          <MapPinned className="h-3 w-3 mt-0.5 shrink-0" />
          {addressParts}
        </p>
      )}
    </div>
  );
}
